import { partnerCategories, partnerListSchema, type Partner, type PartnerCategory } from '@koderek/shared';
import { Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../api/client';
import { t } from '../../texts/pl';
import classes from './partners.module.css';

export function usePartners() {
  return useQuery({ queryKey: ['partners'], queryFn: () => apiGet('/partners', partnerListSchema), staleTime: 5 * 60_000 });
}

/** Partnerzy pogrupowani w sekcje w stałej kolejności; puste sekcje pomijamy. */
export function groupPartners(partners: Partner[]): { category: PartnerCategory; partners: Partner[] }[] {
  return partnerCategories
    .map((category) => ({ category, partners: partners.filter((p) => p.category === category) }))
    .filter((group) => group.partners.length);
}

/**
 * Partnerzy wydarzenia na dole strony startowej i listy edycji (PRD §10 pkt 38). Bez partnerów – nic (także przy błędzie,
 * żeby nie psuć strony).
 */
export function PartnersSection() {
  const partners = usePartners();
  const groups = groupPartners(partners.data?.partners ?? []);
  if (!groups.length) return null;
  return (
    <section className={classes.section} aria-label={t.partners.sectionLabel}>
      <Stack gap="xl">
        {groups.map((group) => (
          <Stack key={group.category} gap="md" align="center">
            <Title order={2} size="h4" className={classes.heading}>
              {t.partners.categories[group.category]}
            </Title>
            <div className={classes.logos}>
              {group.partners.map((partner) => (
                <PartnerLogo key={partner.id} partner={partner} />
              ))}
            </div>
          </Stack>
        ))}
      </Stack>
    </section>
  );
}

function PartnerLogo({ partner }: { partner: Partner }) {
  const image = <img src={partner.logoUrl} alt={partner.name} className={classes.logo} loading="lazy" />;
  return (
    <div className={classes.item} title={partner.name}>
      {partner.url ? (
        <a href={partner.url} target="_blank" rel="noopener noreferrer" className={classes.link}>
          {image}
        </a>
      ) : (
        image
      )}
      <Text size="xs" c="dimmed" ta="center" className={classes.name}>
        {partner.name}
      </Text>
    </div>
  );
}
